import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  BillingProcess,
  BillingProcessInvoicePreview,
  BillingProcessItem,
  BillingProcessSourceType,
} from 'src/app/models/billing-process';
import { Marina } from 'src/app/models/marina';
import { MarinaCompany } from 'src/app/models/marina-company';
import { BillingProcessService } from 'src/app/services/billing-process.service';
import { StorageUtil } from 'src/app/utils/storage.util';
import { MessageUtil } from 'src/app/utils/message.util';

interface SourceSummary {
  key: BillingProcessSourceType;
  title: string;
  icon: string;
  count: number;
  total: number;
}

@Component({
  selector: 'app-wizard-step-confirmation',
  templateUrl: './wizard-step-confirmation.component.html',
  styleUrls: ['./wizard-step-confirmation.component.scss'],
})
export class WizardStepConfirmationComponent implements OnInit {
  @Input() process: BillingProcess;
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  items: BillingProcessItem[] = [];
  preview: BillingProcessInvoicePreview | null = null;
  loading = false;

  marina: Marina | null = null;
  marinaCompanies: MarinaCompany[] = [];

  private readonly monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];

  constructor(
    private billingProcessService: BillingProcessService,
    private spinner: NgxSpinnerService,
    private messageService: MessageUtil,
  ) {}

  ngOnInit(): void {
    this.marina = StorageUtil.getMarina();
    this.marinaCompanies = this.marina?.marinaCompanies ?? [];
    this.load();
  }

  load(): void {
    this.loading = true;
    this.spinner.show();
    forkJoin({
      items: this.billingProcessService.listItems(this.process.id),
      preview: this.billingProcessService.invoicePreview(this.process.id),
    }).subscribe({
      next: ({ items, preview }) => {
        this.items = items ?? [];
        this.preview = preview;
        this.spinner.hide();
        this.loading = false;
      },
      error: (err) => {
        this.spinner.hide();
        this.loading = false;
        this.messageService.generateMessage(
          'error', 'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao carregar dados de confirmação.');
      },
    });
  }

  get periodLabel(): string {
    const m = this.monthNames[this.process.referenceMonth - 1] ?? this.process.referenceMonth;
    return `${m}/${this.process.referenceYear}`;
  }

  get franchisePeriodLabel(): string | null {
    if (!this.process.franchiseReferenceMonth || !this.process.franchiseReferenceYear) return null;
    const m = this.monthNames[this.process.franchiseReferenceMonth - 1] ?? this.process.franchiseReferenceMonth;
    return `${m}/${this.process.franchiseReferenceYear}`;
  }

  get companyLabel(): string {
    if (this.process.marinaCompanyId == null) {
      return this.marina?.companyName ?? this.marina?.tradeName ?? 'Empresa principal';
    }
    const mc = this.marinaCompanies.find((c) => c.id === this.process.marinaCompanyId);
    return mc?.companyFederalName ?? `Empresa #${this.process.marinaCompanyId}`;
  }

  private buildSummary(key: BillingProcessSourceType, title: string, icon: string): SourceSummary {
    const list = this.items.filter((i) =>
      i.sourceType === key && i.selected && !i.alreadyGenerated && !i.emitterBlocked);
    return {
      key, title, icon,
      count: list.length,
      total: list.reduce((s, i) => s + (Number(i.computedTotal) || 0), 0),
    };
  }

  get summaries(): SourceSummary[] {
    return [
      this.buildSummary('BOAT_SERVICE', 'Mensalidades de embarcação', 'pi pi-shield'),
      this.buildSummary('CUSTOMER_SERVICE', 'Serviços de cliente', 'pi pi-users'),
      this.buildSummary('FRANCHISE', 'Franquias', 'pi pi-ticket'),
      this.buildSummary('EXISTING_ORDER', 'Pedidos avulsos existentes', 'pi pi-file-edit'),
    ];
  }

  get totalOrders(): number {
    return this.summaries.reduce((s, x) => s + x.count, 0);
  }

  get totalOrdersValue(): number {
    return this.summaries.reduce((s, x) => s + x.total, 0);
  }

  get lockedCount(): number {
    return this.items.filter((i) => i.alreadyGenerated || i.emitterBlocked).length;
  }

  get alreadyGeneratedCount(): number {
    return this.items.filter((i) => i.alreadyGenerated).length;
  }

  get emitterBlockedCount(): number {
    return this.items.filter((i) => i.emitterBlocked).length;
  }

  get hasMultiCompany(): boolean {
    return !!this.preview && this.preview.byCompany && this.preview.byCompany.length > 1;
  }

  get settings() {
    return this.process.invoiceSettings ?? null;
  }

  get skipInvoiceGeneration(): boolean {
    return !!this.settings?.skipInvoiceGeneration;
  }

  formatDate(s: string | null | undefined): string {
    if (!s) return '—';
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return s;
    return `${m[3]}/${m[2]}/${m[1]}`;
  }

  get dueDateLabel(): string {
    if (!this.settings) return '—';
    if (this.settings.dueDate) {
      return `Vencimento único: ${this.formatDate(this.settings.dueDate)}`;
    }
    if (this.settings.dueDateMonth && this.settings.dueDateYear) {
      const m = this.monthNames[this.settings.dueDateMonth - 1] ?? this.settings.dueDateMonth;
      return `Dia do cliente em ${m}/${this.settings.dueDateYear}`;
    }
    return 'Não definido';
  }

  get canConfirm(): boolean {
    return !this.loading && this.totalOrders > 0;
  }
}
